/**
 * Hook to fetch real dashboard statistics from Supabase
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { Logger, LogCategory } from '@/utils/logger';
import type { DashboardStats } from '@/types';

export function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const company = useAuthStore((state) => state.company);

  useEffect(() => {
    if (!company?.id) {
      setIsLoading(false);
      return;
    }

    async function fetchStats() {
      if (!company) return;

      try {
        Logger.info(LogCategory.DATA, 'Fetching dashboard stats', 'useDashboardStats', {
          companyId: company.id,
        });

        // Fetch phone numbers stats
        const { data: phoneNumbers, error: phoneError } = await supabase
          .from('phone_numbers')
          .select('id, flag_status')
          .eq('company_id', company.id)
          .eq('is_archived', false);

        if (phoneError) throw phoneError;

        const totalPhoneNumbers = phoneNumbers?.length || 0;
        const flaggedNumbers = phoneNumbers?.filter((p: any) => p.flag_status === 'flagged').length || 0;
        const cleanNumbers = totalPhoneNumbers - flaggedNumbers;
        const flaggedPercentage = totalPhoneNumbers > 0 ? (flaggedNumbers / totalPhoneNumbers) * 100 : 0;

        // Fetch activity stats for last 24h, 7d, 30d
        const now = new Date();
        const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const last30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

        const [activity24h, activity7d, activity30d] = await Promise.all([
          supabase
            .from('phone_activity')
            .select('id', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .gte('created_at', last24h),
          supabase
            .from('phone_activity')
            .select('id', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .gte('created_at', last7d),
          supabase
            .from('phone_activity')
            .select('id', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .gte('created_at', last30d),
        ]);

        // Fetch billing/credits stats
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        const { data: billingData, error: billingError } = await supabase
          .from('billing_history')
          .select('credits')
          .eq('company_id', company.id)
          .gte('created_at', firstDayOfMonth);

        if (billingError) {
          Logger.warn(LogCategory.DATA, 'Failed to fetch billing data', 'useDashboardStats', undefined, billingError);
        }

        const creditsUsedThisMonth = billingData?.reduce((sum, b: any) => sum + (b.credits || 0), 0) || 0;

        const dashboardStats: DashboardStats = {
          totalPhoneNumbers,
          cleanNumbers,
          flaggedNumbers,
          flaggedPercentage,
          last24hActivity: activity24h.count || 0,
          last7dActivity: activity7d.count || 0,
          last30dActivity: activity30d.count || 0,
          creditsRemaining: company.credits || 0,
          creditsUsedThisMonth,
        };

        setStats(dashboardStats);
        setError(null);

        Logger.info(LogCategory.DATA, 'Dashboard stats fetched successfully', 'useDashboardStats', {
          totalPhoneNumbers,
          flaggedNumbers,
        });
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to fetch dashboard stats', 'useDashboardStats', undefined, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setIsLoading(false);
      }
    }

    fetchStats();
  }, [company?.id]);

  return { stats, isLoading, error };
}
